import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, PawPrint, Leaf, Dna, ShieldCheck, Heart, Facebook, Instagram } from "lucide-react";
import api from "@/lib/api";
import KineticHero from "@/components/KineticHero";
import EditorialMarquee from "@/components/EditorialMarquee";
import ProductCard from "@/components/ProductCard";
import Manifesto from "@/components/Manifesto";

const CATEGORY_META = [
  { name: "Recovery", blurb: "BPC-157, TB-500 and tissue-repair compounds" },
  { name: "Growth", blurb: "GH secretagogues and growth-promoting peptides" },
  { name: "Anti-Aging", blurb: "Telomerase activators and longevity compounds" },
  { name: "Metabolic", blurb: "Metabolic research peptides and compounds" },
  { name: "Stacks", blurb: "The Wolverine & Hulk signature peptide stacks" },
  { name: "Cognitive", blurb: "Semax, Selank and nootropic research peptides" },
  { name: "Wellness", blurb: "Glutathione, B12 and general wellness compounds" },
  { name: "Ketones", blurb: "K-1 pure therapeutic ketones via our Pruvit store" },
  { name: "Merch", blurb: "Official We Are SFH tees & tanks" },
];

const TikTokIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" />
  </svg>
);

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setFeatured(r.data)).catch(() => {});
  }, []);

  return (
    <main>
      <KineticHero />
      <EditorialMarquee />

      <section data-testid="featured-section" className="mx-auto max-w-7xl px-6 py-24 md:py-32">
        <div className="mb-14 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="font-mono text-[11px] tracking-[0.35em] text-[#00ff72]">BESTSELLERS</p>
            <h2 className="mt-3 font-display text-5xl tracking-tight text-[#F9F6F0] md:text-7xl">
              Featured <span className="italic text-[#666666]">Peptides</span>
            </h2>
          </div>
          <Link
            to="/products"
            data-testid="featured-view-all-link"
            className="group flex items-center gap-2 font-mono text-xs tracking-[0.2em] text-[#A3A3A3] transition-colors hover:text-[#00ff72]"
          >
            VIEW FULL CATALOG
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {featured.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>

      <section data-testid="categories-section" className="border-t border-white/10 bg-[#0A0A0A]">
        <div className="mx-auto max-w-7xl px-6 py-24 md:py-32">
          <p className="font-mono text-[11px] tracking-[0.35em] text-[#F9F6F0]">THE COLLECTION</p>
          <h2 className="mb-14 mt-3 font-display text-5xl tracking-tight text-[#F9F6F0] md:text-7xl">
            Research <span className="italic text-[#666666]">Categories</span>
          </h2>
          <div className="grid gap-px border border-white/10 bg-white/10 md:grid-cols-2">
            {CATEGORY_META.map((c, i) => {
              const green = i % 2 === 0;
              const accent = green ? "text-[#00ff72]" : "text-[#F9F6F0]";
              const hoverAccent = green ? "group-hover:text-[#00ff72]" : "group-hover:text-[#F9F6F0]";
              return (
              <motion.div
                key={c.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
              >
                <Link
                  to={`/products?category=${c.name}`}
                  data-testid={`category-link-${c.name.toLowerCase()}`}
                  className="group flex h-full flex-col justify-between bg-[#0A0A0A] p-10 transition-colors duration-300 hover:bg-[#161616]"
                >
                  <div className="flex items-start justify-between">
                    <span className={`font-mono text-xs ${accent}`}>0{i + 1}</span>
                    <ArrowUpRight className={`h-5 w-5 text-[#666666] transition-all group-hover:-translate-y-1 group-hover:translate-x-1 ${hoverAccent}`} />
                  </div>
                  <div className="mt-16">
                    <h3 className={`font-display text-3xl tracking-tight transition-colors md:text-4xl ${accent}`}>
                      {c.name.toUpperCase()}
                    </h3>
                    <p className="mt-3 max-w-xs text-sm text-[#A3A3A3]">{c.blurb}</p>
                    <p className={`mt-6 font-mono text-[10px] tracking-[0.3em] text-[#666666] transition-colors ${hoverAccent}`}>
                      EXPLORE &#8594;
                    </p>
                  </div>
                </Link>
              </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <Manifesto />

      <section data-testid="flyers-section" className="border-t border-white/10">
        <div className="mx-auto max-w-7xl px-6 py-24 md:py-32">
          <p className="font-mono text-[11px] tracking-[0.35em] text-[#F9F6F0]">FROM SFH BIOHEALTH</p>
          <h2 className="mb-14 mt-3 font-display text-5xl tracking-tight text-[#F9F6F0] md:text-7xl">
            Latest <span className="italic text-[#666666]">Flyers</span>
          </h2>
          <div className="grid gap-6 md:grid-cols-2">
            {[
              { src: "/flyer1.png", label: "ADVANCED PEPTIDES — STACKS, NAD+ & KPV" },
              { src: "/flyer3.png", label: "THE FULL PEPTIDE LINEUP" },
            ].map((f, i) => (
              <motion.figure
                key={f.src}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: i * 0.12, ease: [0.76, 0, 0.24, 1] }}
                className="group overflow-hidden border border-white/10"
              >
                <img
                  src={f.src}
                  alt={f.label}
                  loading="lazy"
                  className="w-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                />
                <figcaption className="border-t border-white/10 bg-[#0A0A0A] px-5 py-3 font-mono text-[10px] tracking-[0.25em] text-[#A3A3A3]">
                  {f.label}
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      <section id="pep-daddy" data-testid="pep-daddy-section" className="border-t border-white/10 bg-[#0A0A0A]">
        <div className="mx-auto max-w-7xl px-6 py-24 md:py-32">
          <div className="grid items-start gap-12 lg:grid-cols-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
              className="lg:col-span-6"
            >
              <p className="font-mono text-[11px] tracking-[0.35em] text-[#00ff72]">
                SCIENCE BACKED. NATURALLY POWERED. PET APPROVED.
              </p>
              <h2 className="mt-4 font-display text-6xl leading-[0.9] tracking-tight text-[#F9F6F0] md:text-8xl">
                PEP DADDY
              </h2>
              <p className="mt-2 flex items-center gap-3 font-display text-3xl italic text-[#00ff72] md:text-4xl">
                for pets <PawPrint className="h-7 w-7" />
              </p>
              <p className="mt-6 max-w-md text-sm leading-relaxed text-[#A3A3A3]">
                Peptides to keep your furry friends healthy, stronger, and live a happier, longer
                life. Research-grade compounds, tailored protocols for your pet&apos;s unique needs.
              </p>

              <div className="mt-10 grid grid-cols-1 gap-px border border-white/10 bg-white/10 sm:grid-cols-2">
                {[
                  { icon: Leaf, title: "WHOLE PET WELLNESS", text: "Supports energy, immunity, mobility & overall vitality." },
                  { icon: Dna, title: "BIOPEPTIDE THERAPY", text: "Advanced peptides to support healing, comfort & longevity." },
                  { icon: ShieldCheck, title: "IMMUNE & GUT HEALTH", text: "Strengthen from within for a happier, healthier life." },
                  { icon: Heart, title: "PERSONALIZED CARE", text: "Tailored protocols for your pet's unique needs." },
                ].map((b) => (
                  <div key={b.title} className="bg-[#0A0A0A] p-6 transition-colors hover:bg-[#111111]">
                    <b.icon className="h-5 w-5 text-[#00ff72]" />
                    <p className="mt-3 font-mono text-[11px] tracking-[0.2em] text-[#F9F6F0]">{b.title}</p>
                    <p className="mt-2 text-xs leading-relaxed text-[#A3A3A3]">{b.text}</p>
                  </div>
                ))}
              </div>

              <div className="mt-8 grid grid-cols-2 gap-6 border-t border-white/10 pt-6 sm:grid-cols-4">
                {["MORE ENERGY", "BETTER HEALTH", "LESS PAIN", "LONGER LIFE"].map((s) => (
                  <p key={s} className="font-mono text-[10px] tracking-[0.2em] text-[#A3A3A3]">{s}</p>
                ))}
              </div>

              <div className="mt-8">
                <p className="mb-3 font-mono text-[10px] tracking-[0.3em] text-[#666666]">
                  SHOP THE PET RESEARCH LINEUP
                </p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { id: "tb-500", label: "TB-500" },
                    { id: "bpc-157", label: "BPC-157" },
                    { id: "kpv", label: "KPV" },
                    { id: "ghk-cu", label: "GHK-CU" },
                  ].map((l) => (
                    <Link
                      key={l.id}
                      to={`/products/${l.id}`}
                      data-testid={`pepdaddy-product-${l.id}`}
                      className="group flex items-center gap-2 border border-[#00ff72]/40 px-4 py-2 font-mono text-[11px] tracking-[0.2em] text-[#00ff72] transition-colors hover:bg-[#00ff72] hover:text-black"
                    >
                      {l.label}
                      <ArrowUpRight className="h-3 w-3 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                    </Link>
                  ))}
                </div>
              </div>

              <p className="mt-10 font-display text-3xl italic text-[#F9F6F0] md:text-4xl">
                Stronger Bonds. <span className="text-[#00ff72]">Longer Lives.</span>
              </p>
              <div className="mt-6 flex flex-wrap gap-4">
                <a
                  href="https://www.facebook.com/p/SFH-Biohealth-and-Wellness-61580573643723/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="pepdaddy-facebook-link"
                  className="flex items-center gap-2 border border-white/20 px-5 py-3 font-mono text-[11px] tracking-[0.2em] text-[#F9F6F0] transition-colors hover:border-[#00ff72] hover:text-[#00ff72]"
                >
                  <Facebook className="h-4 w-4" /> FACEBOOK
                </a>
                <a
                  href="https://www.instagram.com/sfh_biohealth_and_wellness/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="pepdaddy-instagram-link"
                  className="flex items-center gap-2 border border-white/20 px-5 py-3 font-mono text-[11px] tracking-[0.2em] text-[#F9F6F0] transition-colors hover:border-[#00ff72] hover:text-[#00ff72]"
                >
                  <Instagram className="h-4 w-4" /> @SFH_BIOHEALTH_AND_WELLNESS
                </a>
                <a
                  href="https://www.tiktok.com/@sfh_biohealth_wellness"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="pepdaddy-tiktok-link"
                  className="flex items-center gap-2 border border-white/20 px-5 py-3 font-mono text-[11px] tracking-[0.2em] text-[#F9F6F0] transition-colors hover:border-[#00ff72] hover:text-[#00ff72]"
                >
                  <TikTokIcon className="h-4 w-4" /> @SFH_BIOHEALTH_WELLNESS
                </a>
              </div>
              <p
                data-testid="pepdaddy-disclaimer"
                className="mt-8 border-2 border-red-500/50 bg-red-500/10 p-4 font-mono text-[11px] font-medium leading-relaxed tracking-wider text-red-400"
              >
                FOR RESEARCH PURPOSES ONLY. THESE PRODUCTS ARE NOT INTENDED TO DIAGNOSE, TREAT,
                CURE OR PREVENT ANY DISEASE. NOT FOR HUMAN OR VETERINARY CONSUMPTION.
              </p>
            </motion.div>

            <div className="space-y-6 lg:col-span-5 lg:col-start-8">
              {[
                { src: "/products/pep-daddy-2.jpg", label: "PEP DADDY — FOR PETS" },
                { src: "/products/pep-daddy-1.jpg", label: "SCIENCE BACKED. PET APPROVED." },
              ].map((f, i) => (
                <motion.figure
                  key={f.src}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: i * 0.15, ease: [0.76, 0, 0.24, 1] }}
                  className="group overflow-hidden border border-white/10"
                >
                  <img
                    src={f.src}
                    alt={f.label}
                    loading="lazy"
                    className="w-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
                  />
                  <figcaption className="border-t border-white/10 bg-[#050505] px-5 py-3 font-mono text-[10px] tracking-[0.25em] text-[#A3A3A3]">
                    {f.label}
                  </figcaption>
                </motion.figure>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="wholesale" data-testid="wholesale-section" className="border-t border-white/10 bg-[#0A0A0A]">
        <div className="mx-auto max-w-7xl px-6 py-24 md:py-32">
          <div className="grid items-end gap-10 md:grid-cols-12">
            <div className="md:col-span-8">
              <p className="font-mono text-[11px] tracking-[0.35em] text-[#F9F6F0]">INSTITUTIONS &amp; BULK BUYERS</p>
              <h2 className="mt-4 font-display text-5xl leading-[0.95] tracking-tight text-[#F9F6F0] md:text-8xl">
                WHOLESALE<br />
                <span className="italic text-[#666666]">Pricing</span>
              </h2>
            </div>
            <div className="md:col-span-4">
              <p className="text-sm leading-relaxed text-[#A3A3A3]">
                Research institutions and bulk buyers enjoy significant discounts. Contact us for
                wholesale kit pricing — 10 vials per kit.
              </p>
              <a
                href="mailto:info@sfhbiohealthandwellness.com"
                data-testid="wholesale-cta-btn"
                className="group mt-8 inline-flex items-center gap-3 border border-white/25 px-8 py-4 font-mono text-xs tracking-[0.2em] text-[#F9F6F0] transition-colors hover:border-[#00ff72] hover:bg-[#00ff72] hover:text-black"
              >
                ACCESS WHOLESALE
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <EditorialMarquee />
    </main>
  );
}
